import { darken, ThemeOptions } from '@mui/material/styles'

import { ROWS_PER_PAGE_OPTIONS } from 'common/helpers/commonConstants'

export interface NotificationsPalette {
  // The error token fails 4.5:1 against white text, so the badge has its own red.
  badge: string
  title: string
  selectedBackground: string
  selectedBorder: string
  unreadBackground: string
  hoverBackground: string
  divider: string
  accent: string
  unreadDot: string
  iconBackground: string
  danger: string
  // The fill of the app's main buttons, for a surface with white text.
  highlight: string
  highlightHover: string
}

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    notifications: NotificationsPalette
    secondaryBackground: Palette['primary']
    altBackground: Palette['primary']
    primaryText: Palette['primary']
    secondaryText: Palette['primary']
    altText: Palette['primary']
    instanceManagerMarker: Palette['primary']
    shadows: Palette['primary']
    secondaryShadow: Palette['primary']
    folderIcon: Palette['primary']
    fileIcon: Palette['primary']
    black: Palette['primary']
    white: Palette['primary']
    nodeText: Palette['primary']
    basicNodeSelectedBg: Palette['primary']
    dataReadingSelectedBg: Palette['primary']
    outputSelectedBg: Palette['primary']
    reportSelectedBg: Palette['primary']
    indexSelectedBg: Palette['primary']
    inputDataArraySelectedBg: Palette['primary']
    moduleSelectedBg: Palette['primary']
    inputDataArrayIcon: Palette['primary']
    indexHierarchyIcon: Palette['primary']
    tableHeader: Palette['primary']
    tableData: Palette['primary']
    tableDataDimmed: Palette['primary']
    tableEditableData: Palette['primary']
    tableEditableDataDimmed: Palette['primary']
    widgetBackground: Palette['primary']
    altWidgetBackground: Palette['primary']
    taskStatusWarning: Palette['primary']
    taskStatusSuccess: Palette['primary']
    toggleButtonBg: Palette['primary']
    folderBg: Palette['primary']
    hightLightOption: Palette['primary']
    resizerDivider: Palette['primary']
    backgroundPlaceholder: Palette['primary']
    zipIcon: Palette['primary']
    dbIcon: Palette['primary']
    csvIcon: Palette['primary']
    jsonIcon: Palette['primary']
    templateIcon: Palette['primary']
    oldIcon: Palette['primary']
    defaultIcon: Palette['primary']
    disabledAction: Palette['primary']
    primaryOnly: Palette['primary']
    darkGrey: Palette['primary']
    leftChat: Palette['primary']
    rightChat: Palette['primary']
    tabHoverColor: Palette['primary']
    variableNode: Palette['primary']
    readDataNode: Palette['primary']
    inputNode: Palette['primary']
    userTitle: Palette['primary']
    lightGrey: Palette['primary']
    widgetDivider: Palette['primary']
    whiteBlackContrast: Palette['primary']
    companyCard: Palette['primary']
    tdAutocompleteArrow: Palette['primary']
    defaultTableHeaderColor: Palette['primary']
    defaultTableHeaderBackgroundColor: Palette['primary']
    defaultHeaderBackgroundColor: Palette['primary']
    defaultHeaderColor: Palette['primary']
    itemheaderColor: Palette['primary']
    itemHeaderBackground: Palette['primary']
    itemHeaderIndexBackground: Palette['primary']
    badgeColor: Palette['primary']
    homeText: Palette['primary']
    homeBox: Palette['primary']
    topBar: Palette['primary']
    workspaceBar: Palette['primary']
    notificationTitle: Palette['primary']
    appCardShadow: { value: string }
    itemWidgetShadow: { value: string }
    homeMainBox: Palette['primary']
    textFieldBorder: Palette['primary']
    filterComponent: Palette['primary']
    errorInDefinitionIcon: Palette['primary']
    notificationCardComponent: Palette['primary']
    measureColor: Palette['primary']
    baseChipColor: Palette['primary']
    pivotBadge: Palette['primary']
    tagText: Palette['primary']
    linearProgress: Palette['primary']
    diagramWidget: Palette['primary']
    borderWidget: Palette['primary']
    tableCellColor: Palette['primary']
    tableCellBorder: Palette['primary']
    menuItemSelected: Palette['primary']
    highlightLog: Palette['primary']
    currentHighLight: Palette['primary']
    profileCardShadow: Palette['primary']
    blackAndWhite: Palette['primary']
    warningCard: Palette['primary']
    successCard: Palette['primary']
    infoCard: Palette['primary']
    errorCard: Palette['primary']
    widgetWrapperErrorCard: Palette['primary']
    htmlEditorBase: Palette['primary']
    currentRowColumnHighlight: Palette['primary']
    currentRowColumnHighlightBorder: Palette['primary']
    successTestStatus: Palette['primary']
    successTestStatusChip: Palette['primary']
    failedTestStatus: Palette['primary']
    licenseInfoContainer: Palette['primary']
    disabledButtonBackground: Palette['primary']
    codeWidgetHeader: Palette['primary']
  }
  interface PaletteOptions {
    secondaryBackground: PaletteOptions['primary']
    altBackground: PaletteOptions['primary']
    primaryText: PaletteOptions['primary']
    secondaryText: PaletteOptions['primary']
    altText: PaletteOptions['primary']
    shadows: PaletteOptions['primary']
    instanceManagerMarker: PaletteOptions['primary']
    secondaryShadow: PaletteOptions['primary']
    folderIcon: PaletteOptions['primary']
    fileIcon: PaletteOptions['primary']
    black: PaletteOptions['primary']
    white: PaletteOptions['primary']
    nodeText: PaletteOptions['primary']
    basicNodeSelectedBg: PaletteOptions['primary']
    dataReadingSelectedBg: PaletteOptions['primary']
    outputSelectedBg: PaletteOptions['primary']
    reportSelectedBg: PaletteOptions['primary']
    indexSelectedBg: PaletteOptions['primary']
    inputDataArraySelectedBg: PaletteOptions['primary']
    inputDataArrayIcon: PaletteOptions['primary']
    indexHierarchyIcon: PaletteOptions['primary']
    moduleSelectedBg: PaletteOptions['primary']
    tableHeader: PaletteOptions['primary']
    tableData: PaletteOptions['primary']
    tableDataDimmed: PaletteOptions['primary']
    tableEditableData: PaletteOptions['primary']
    tableEditableDataDimmed: PaletteOptions['primary']
    widgetBackground: PaletteOptions['primary']
    altWidgetBackground: PaletteOptions['primary']
    taskStatusWarning: PaletteOptions['primary']
    taskStatusSuccess: PaletteOptions['primary']
    toggleButtonBg: PaletteOptions['primary']
    folderBg: PaletteOptions['primary']
    hightLightOption: PaletteOptions['primary']
    resizerDivider: PaletteOptions['primary']
    backgroundPlaceholder: PaletteOptions['primary']
    zipIcon: PaletteOptions['primary']
    dbIcon: PaletteOptions['primary']
    csvIcon: PaletteOptions['primary']
    jsonIcon: PaletteOptions['primary']
    templateIcon: PaletteOptions['primary']
    oldIcon: PaletteOptions['primary']
    defaultIcon: PaletteOptions['primary']
    disabledAction: PaletteOptions['primary']
    primaryOnly: PaletteOptions['primary']
    darkGrey: PaletteOptions['primary']
    leftChat: PaletteOptions['primary']
    rightChat: PaletteOptions['primary']
    tabHoverColor: PaletteOptions['primary']
    variableNode: PaletteOptions['primary']
    readDataNode: PaletteOptions['primary']
    inputNode: PaletteOptions['primary']
    userTitle: PaletteOptions['primary']
    lightGrey: PaletteOptions['primary']
    widgetDivider: PaletteOptions['primary']
    whiteBlackContrast: PaletteOptions['primary']
    companyCard: PaletteOptions['primary']
    tdAutocompleteArrow: PaletteOptions['primary']
    defaultTableHeaderColor: PaletteOptions['primary']
    defaultHeaderColor: PaletteOptions['primary']
    defaultTableHeaderBackgroundColor: PaletteOptions['primary']
    defaultHeaderBackgroundColor: PaletteOptions['primary']
    itemheaderColor: PaletteOptions['primary']
    itemHeaderBackground: PaletteOptions['primary']
    itemHeaderIndexBackground: PaletteOptions['primary']
    badgeColor: PaletteOptions['primary']
    homeText: PaletteOptions['primary']
    homeBox: PaletteOptions['primary']
    topBar: PaletteOptions['primary']
    workspaceBar: PaletteOptions['primary']
    notificationTitle: PaletteOptions['primary']
    appCardShadow: { value: string }
    itemWidgetShadow: { value: string }
    notifications: NotificationsPalette
    homeMainBox: PaletteOptions['primary']
    textFieldBorder: PaletteOptions['primary']
    filterComponent: PaletteOptions['primary']
    errorInDefinitionIcon: PaletteOptions['primary']
    notificationCardComponent: PaletteOptions['primary']
    measureColor: PaletteOptions['primary']
    baseChipColor: PaletteOptions['primary']
    pivotBadge: PaletteOptions['primary']
    tagText: PaletteOptions['primary']
    linearProgress: PaletteOptions['primary']
    diagramWidget: PaletteOptions['primary']
    borderWidget: PaletteOptions['primary']
    tableCellColor: PaletteOptions['primary']
    tableCellBorder: PaletteOptions['primary']
    menuItemSelected: PaletteOptions['primary']
    highlightLog: PaletteOptions['primary']
    currentHighLight: PaletteOptions['primary']
    profileCardShadow: PaletteOptions['primary']
    blackAndWhite: PaletteOptions['primary']
    warningCard: PaletteOptions['primary']
    successCard: PaletteOptions['primary']
    infoCard: PaletteOptions['primary']
    errorCard: PaletteOptions['primary']
    widgetWrapperErrorCard: PaletteOptions['primary']
    htmlEditorBase: PaletteOptions['primary']
    currentRowColumnHighlight: PaletteOptions['primary']
    currentRowColumnHighlightBorder: PaletteOptions['primary']
    successTestStatus: PaletteOptions['primary']
    successTestStatusChip: PaletteOptions['primary']
    failedTestStatus: PaletteOptions['primary']
    licenseInfoContainer: PaletteOptions['primary']
    disabledButtonBackground: PaletteOptions['primary']
    codeWidgetHeader: PaletteOptions['primary']
  }
}

const lightTheme: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920
    }
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#368EE0'
    },
    tableCellColor: {
      main: 'rgba(0,0,0,0.8)'
    },
    tableCellBorder: {
      main: '#E0E0E0'
    },
    licenseInfoContainer: {
      main: 'rgba(0, 0, 0, .2)'
    },
    disabledButtonBackground: {
      main: '#F3F3F3'
    },
    codeWidgetHeader: {
      main: '#F8F8F8'
    },
    blackAndWhite: {
      main: '#000'
    },
    diagramWidget: {
      main: '#fff'
    },
    borderWidget: {
      main: 'rgba(0, 0, 0, 0.12)'
    },
    tabHoverColor: {
      main: '#F2F2F2'
    },
    warningCard: {
      main: '#FFFAF4',
      contrastText: '#F27500'
    },
    successCard: {
      main: '#F3FDF5',
      contrastText: '#359149'
    },
    infoCard: {
      main: '#F1EFF9',
      contrastText: '#2933AF'
    },
    htmlEditorBase: {
      main: '#e6e6e6',
      contrastText: 'rgba(0,0,0,.5)',
      dark: darken('rgba(189, 189, 189, 0.5)', 0.5),
      light: 'rgba(189, 189, 189,.8)'
    },
    errorCard: {
      main: '#FFF9F9',
      contrastText: '#C3504E'
    },
    widgetWrapperErrorCard: {
      main: '#FFFAF4'
    },
    highlightLog: {
      main: '#C3E2FE'
    },
    currentHighLight: {
      main: '#5EB1FC'
    },
    linearProgress: {
      main: 'rgb(238, 238, 238)'
    },
    baseChipColor: {
      main: 'rgba(0, 0, 0, 0.08)'
    },
    measureColor: {
      main: 'rgba(0, 0, 0, 0.20)',
      light: '#fff'
    },
    menuItemSelected: {
      main: '#C3E2FE'
    },
    defaultHeaderBackgroundColor: {
      main: '#fff'
    },
    notificationCardComponent: {
      main: '#fff'
    },
    defaultTableHeaderBackgroundColor: {
      main: '#25639C'
    },
    pivotBadge: {
      main: '#25639C'
    },
    appCardShadow: {
      value:
        ' 0 3px 1px -2px rgba(181, 181, 181, 0.2), 0 1px 5px 0 rgba(73, 73, 73, 0.15), 0 2px 2px 0 rgba(181, 181, 181, 0.14)'
    },
    itemWidgetShadow: {
      value: '0 4px 20px rgba(24, 39, 75, 0.10), 0 1px 4px rgba(24, 39, 75, 0.06)'
    },
    notifications: {
      badge: '#D9000D',
      title: '#014F98',
      selectedBackground: '#C3E2FE',
      selectedBorder: '#368EE0',
      unreadBackground: '#F3F9FF',
      hoverBackground: '#F7FBFF',
      divider: '#E1F0FF',
      accent: '#014F98',
      unreadDot: '#368EE0',
      iconBackground: '#E1F0FF',
      danger: '#D9000D',
      highlight: '#368EE0',
      highlightHover: '#014F98'
    },
    homeText: {
      main: '#3C3A3A'
    },
    defaultTableHeaderColor: {
      main: '#fff'
    },
    homeBox: {
      main: '#F5F5F5'
    },
    topBar: {
      main: '#0E152C'
    },
    workspaceBar: {
      main: '#d6e2eb'
    },
    errorInDefinitionIcon: {
      main: '#ff0000'
    },
    homeMainBox: {
      main: '#FFFFFF'
    },
    textFieldBorder: {
      main: 'rgba(0, 0, 0, 0.25)'
    },
    notificationTitle: {
      main: '#676767'
    },
    defaultHeaderColor: {
      main: '#014F98'
    },
    itemheaderColor: {
      main: '#616161'
    },
    itemHeaderBackground: {
      main: '#F8F8F8'
    },
    itemHeaderIndexBackground: {
      main: '#F8F8F8'
    },
    tdAutocompleteArrow: {
      main: '#BDBDBD'
    },
    badgeColor: {
      main: '#66a4de'
    },
    userTitle: {
      main: '#368EE0'
    },
    companyCard: {
      main: '#fff'
    },
    lightGrey: {
      main: '#AAAAAA'
    },
    primaryOnly: {
      main: '#368EE0'
    },
    darkGrey: {
      main: 'rgba(0,0,0,0.5)'
    },
    disabledAction: {
      main: 'rgba(138,151,152,0.33)'
    },
    toggleButtonBg: {
      main: '#014F98'
    },
    resizerDivider: {
      main: '#000000'
    },
    hightLightOption: {
      main: 'rgba(0, 0, 0, 0.08)'
    },
    folderBg: {
      main: '#747474'
    },
    profileCardShadow: {
      main: '#00000029'
    },
    primaryText: {
      main: '#014F98',
      light: '#ededed',
      dark: 'rgb(1 79 152)'
    },
    secondaryBackground: {
      main: '#F8FAFE',
      dark: '#edf3ff',
      light: '#014F982C'
    },
    altBackground: {
      main: '#C3E2FF',
      light: '#ededed',
      dark: '#b0cde8'
    },
    secondary: {
      main: '#C3E2FE'
    },
    taskStatusWarning: {
      main: '#f5f514',
      dark: '#57570f'
    },
    taskStatusSuccess: {
      main: '#127517',
      dark: 'rgb(10 70 13)'
    },
    successTestStatus: {
      main: '#127517',
      dark: 'rgb(10 70 13)'
    },
    successTestStatusChip: {
      main: '#127517'
    },
    failedTestStatus: {
      main: '#FF0D15'
    },
    black: {
      main: 'rgba(0,0,0,0.8)'
    },
    white: {
      main: '#fff'
    },
    secondaryText: {
      main: '#707070'
    },
    altText: {
      main: '#4B4B4B'
    },
    error: {
      main: '#FF0D15'
    },
    instanceManagerMarker: {
      main: '#ff7f0e'
    },
    shadows: {
      main: '#7EBCF5'
    },
    secondaryShadow: {
      main: '#00000029'
    },
    background: {
      default: '#FFF',
      paper: '#fff'
    },
    folderIcon: {
      main: '#A705BF'
    },
    fileIcon: {
      main: '#3F4CC2'
    },
    nodeText: {
      main: '#4B4B4B'
    },
    basicNodeSelectedBg: {
      main: '#D9EEF7'
    },
    dataReadingSelectedBg: {
      main: '#DDF0DB'
    },
    outputSelectedBg: {
      main: '#F9DCEA'
    },
    reportSelectedBg: {
      main: '#EFEFEF'
    },
    indexSelectedBg: {
      main: '#EBEBFF'
    },
    inputDataArraySelectedBg: {
      main: '#FAEBD7'
    },
    moduleSelectedBg: {
      main: '#D9E4FC'
    },
    inputDataArrayIcon: {
      main: '#5E4C10'
    },
    indexHierarchyIcon: {
      main: '#5E4C10'
    },
    tableHeader: {
      main: '#EBEBEB',
      contrastText: '#777777',
      dark: '#d5d5d5'
    },
    tableData: {
      main: '#FFFFFF',
      contrastText: '#777777'
    },
    tableDataDimmed: {
      main: 'rgba(255, 255, 255, 0.6)',
      contrastText: 'rgba(119, 119, 119, 0.6)'
    },
    tableEditableData: {
      main: '#FFF2CC',
      contrastText: '#373737'
    },
    tableEditableDataDimmed: {
      main: 'rgba(255, 242, 204, 0.6)',
      contrastText: 'rgba(55, 55, 55, 0.6)'
    },
    widgetBackground: {
      main: '#FFF'
    },
    altWidgetBackground: {
      main: '#E1F0FF',
      contrastText: '#368EE0'
    },
    backgroundPlaceholder: {
      main: '#E1F0FF'
    },
    zipIcon: {
      main: '#009984'
    },
    dbIcon: {
      main: '#F80063'
    },
    csvIcon: {
      main: '#3F4CC2'
    },
    jsonIcon: {
      main: '#BE9C14'
    },
    templateIcon: {
      main: '#25B328'
    },
    oldIcon: {
      main: '#5B7F8E'
    },
    defaultIcon: {
      main: '#FF2F18'
    },
    leftChat: {
      main: '#E1F0FF'
    },
    rightChat: {
      main: '#FFF3CC'
    },
    variableNode: {
      main: '#7AAFC7'
    },
    readDataNode: {
      main: '#7DB87A'
    },
    inputNode: {
      main: '#D4924C'
    },
    widgetDivider: {
      main: 'rgba(0, 0, 0, 0.12)'
    },
    whiteBlackContrast: {
      main: '#000000'
    },
    filterComponent: {
      main: '#f1f5fd'
    },
    tagText: {
      main: 'rgba(0, 0, 0, 0.5)'
    },
    currentRowColumnHighlight: {
      main: 'rgba(0, 0, 0, 0.05)',
      light: 'rgba(0, 0, 0, 0.07)'
    },
    currentRowColumnHighlightBorder: {
      main: '#014F98',
      light: '#014F98'
    }
  },
  typography: {
    fontFamily: ['Roboto', 'sans-serif'].join(','),
    fontSize: 16
  },
  components: {
    MuiTablePagination: {
      defaultProps: {
        rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
        slotProps: {
          select: {
            native: true
          }
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          '*::-webkit-scrollbar': {
            width: '.5rem',
            height: '.5rem'
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: '#00000029'
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: '#808BA4',
            borderRadius: '100px'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF'
        },
        rounded: {
          borderRadius: '5px'
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          height: '48px'
        },
        regular: {
          minHeight: '48px',
          height: '48px',
          '@media (min-width: 600px)': {
            minHeight: '48px'
          }
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        vertical: {
          width: '2px',
          height: '32px',
          margin: '0 6px',
          backgroundColor: '#C3E2FE', //may not be necessary anymore with MUIv5
          borderColor: '#C3E2FE'
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          border: '1px solid #F8FAFE',
          boxShadow: '0px 0px 10px #014F98',
          borderRadius: '4px',
          color: '#014F98',
          backgroundColor: '#FFFFFF'
        }
      }
    },
    MuiButton: {
      defaultProps: {
        fullWidth: true,
        variant: 'contained'
      },
      styleOverrides: {
        outlinedSizeSmall: {
          color: '#014F98',
          backgroundColor: '#FFF',
          border: '1px solid #7EBCF5',
          borderRadius: '4px',
          height: '32px',
          padding: 8,
          textTransform: 'none',
          fontSize: '0.75rem',
          '&:hover': {
            border: '1px solid #014F98',
            backgroundColor: '#C3E2FF'
          },
          '&$disabled': {
            color: '#B1B1B1',
            backgroundColor: '#F3F3F3',
            border: '1px solid #B1B1B1'
          }
        },
        root: {
          //this was label
          textTransform: 'none'
        },
        iconSizeSmall: {
          '& > *:first-of-type': {
            fontSize: 10
          }
        },
        contained: {
          height: '32px',
          backgroundColor: '#368EE0',
          fontSize: '1.1rem',
          color: '#FFF',
          borderRadius: '4px',
          padding: 8,
          border: 'none',
          '&:hover': {
            backgroundColor: '#014F98'
          },
          '&$disabled': {
            color: '#B1B1B1',
            backgroundColor: '#F3F3F3'
          }
        },
        containedSizeSmall: {
          height: '32px',
          fontSize: '0.9rem'
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        colorPrimary: {
          '&:hover': {
            backgroundColor: 'transparent'
          }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#368EE0'
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
        variant: 'outlined',
        size: 'small'
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          // height: 19,
          paddingTop: '6.5px',
          paddingBottom: '6.5px',
          fontSize: '16px',
          lineHeight: '19px',
          letterSpacing: '0px'
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          top: '-4px'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        body: {
          color: '#014F98'
        },
        head: {
          color: '#014F98'
        },
        stickyHeader: {
          backgroundColor: '#F8FAFE'
        }
      }
    },
    MuiLink: {
      defaultProps: {
        underline: 'none'
      }
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small'
      }
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          textDecoration: 'underline',
          textUnderlineOffset: '3px'
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&:has(> input:-webkit-autofill)': {
            backgroundColor: '#e8f0fe',
            '& input': {
              borderRadius: 'unset'
            }
          }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#C3E2FE',
            color: '#014F98'
          },
          '&:hover': {
            backgroundColor: '#e7f3ff',
            color: '#014F98'
          }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          '& .MuiAutocomplete-option': {
            '&.Mui-selected': {
              backgroundColor: '#C3E2FE',
              color: '#014F98'
            },
            '&:hover': {
              backgroundColor: '#e7f3ff',
              color: '#014F98'
            },
            '&[aria-selected="true"]': {
              backgroundColor: '#C3E2FE',
              color: '#014F98',
              '&.Mui-focused': {
                backgroundColor: '#C3E2FE',
                color: '#014F98'
              }
            }
          },
          '& .MuiListItem-root:hover': {
            backgroundColor: '#C3E2FE',
            color: '#014F98'
          }
        }
      }
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          PaperProps: {
            style: {
              maxHeight: '40vh'
            }
          }
        }
      }
    }
  }
}

export default lightTheme
